/**
 * Convert seconds to HH:MM:SS format with optional days.
 */
export function toHHMMSS(seconds: number, decimals = 0): string {
  let secNum = Math.max(0, seconds)

  const days = Math.floor(secNum / 86400)
  secNum %= 86400

  const hours = Math.floor(secNum / 3600)
  secNum %= 3600

  const minutes = Math.floor(secNum / 60)
  secNum %= 60

  const secs = secNum

  const hoursStr = hours.toString().padStart(2, '0')
  const minutesStr = minutes.toString().padStart(2, '0')
  const secsStr = decimals > 0
    ? secs.toFixed(decimals).padStart(3 + decimals, '0')
    : Math.floor(secs).toString().padStart(2, '0')

  let result = `${hoursStr}:${minutesStr}:${secsStr}`

  if (days > 0) {
    const plural = days > 1 ? 's' : ''
    result = `${days} day${plural}, ${result}`
  }

  return result
}

/**
 * Format a number with thousand separators.
 */
export function formatNumber(num: number | string, decimals = 2): string {
  let n = typeof num === 'string' ? parseFloat(num.replace(/[^0-9.-]/g, '')) : num

  if (isNaN(n)) return '0'

  const parts = n.toFixed(decimals).split('.')
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  return parts.join('.')
}

/**
 * Format date for display (short format for mobile).
 */
export function formatDate(dateStr: string, short = false): string {
  const date = new Date(dateStr)

  if (short) {
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return date.toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Format date for datetime-local input.
 */
export function formatDateTimeLocal(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date

  const year = d.getFullYear()
  const month = (d.getMonth() + 1).toString().padStart(2, '0')
  const day = d.getDate().toString().padStart(2, '0')
  const hours = d.getHours().toString().padStart(2, '0')
  const minutes = d.getMinutes().toString().padStart(2, '0')

  return `${year}-${month}-${day}T${hours}:${minutes}`
}

/**
 * Format date for storage (YYYY-MM-DD HH:mm:ss).
 */
export function formatDateForStorage(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date

  const year = d.getFullYear()
  const month = (d.getMonth() + 1).toString().padStart(2, '0')
  const day = d.getDate().toString().padStart(2, '0')
  const hours = d.getHours().toString().padStart(2, '0')
  const minutes = d.getMinutes().toString().padStart(2, '0')
  const seconds = d.getSeconds().toString().padStart(2, '0')

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

/**
 * Get relative time (e.g., "2 hours ago", "just now").
 */
export function getRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSecs < 60) return 'just now'
  if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`

  return formatDate(dateStr, true)
}

/**
 * Convert seconds to compact duration format.
 * - Under 30 days: "23d 5h 37m" (omits zero segments, always at least "0m")
 * - 30+ days: "1M 12d" (months + remaining days)
 * - 365+ days: "1y 2M" (years + remaining months)
 */
export function toCompactDuration(seconds: number): string {
  const secNum = Math.max(0, Math.floor(seconds))

  const totalDays = Math.floor(secNum / 86400)

  if (totalDays >= 365) {
    const years = Math.floor(totalDays / 365)
    const remainingDays = totalDays % 365
    const months = Math.floor(remainingDays / 30)
    const parts: string[] = [`${years}y`]
    if (months > 0) parts.push(`${months}M`)
    return parts.join(' ')
  }

  if (totalDays >= 30) {
    const months = Math.floor(totalDays / 30)
    const remainingDays = totalDays % 30
    const parts: string[] = [`${months}M`]
    if (remainingDays > 0) parts.push(`${remainingDays}d`)
    return parts.join(' ')
  }

  const hours = Math.floor((secNum % 86400) / 3600)
  const minutes = Math.floor((secNum % 3600) / 60)

  const parts: string[] = []
  if (totalDays > 0) parts.push(`${totalDays}d`)
  if (hours > 0) parts.push(`${hours}h`)
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`)

  return parts.join(' ')
}

/** Check if current viewport is mobile width (< 768px, Tailwind md breakpoint) */
export function isMobile(): boolean {
  return typeof window !== 'undefined' && window.innerWidth < 768
}

/**
 * Detect user's region from timezone for locale-aware formatting.
 * Uses Intl API (local browser, no network call).
 */
export type Region = 'AU' | 'UK' | 'US' | 'NZ'

const REGION_STORAGE_KEY = 'ssdl:region'
const VALID_REGIONS: Region[] = ['AU', 'UK', 'US', 'NZ']

let _cachedRegion: Region | null = null

export function detectRegion(): Region {
  if (_cachedRegion) return _cachedRegion

  // Check localStorage for user's manual selection
  try {
    const stored = localStorage.getItem(REGION_STORAGE_KEY) as Region | null
    if (stored && VALID_REGIONS.includes(stored)) {
      _cachedRegion = stored
      return _cachedRegion
    }
  } catch { /* ignore */ }

  // Fall back to timezone detection
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (tz.startsWith('Australia/')) _cachedRegion = 'AU'
    else if (tz.startsWith('Pacific/Auckland') || tz.startsWith('Pacific/Chatham') || tz === 'NZ') _cachedRegion = 'NZ'
    else if (tz.startsWith('Europe/London') || tz === 'Europe/Belfast' || tz === 'Europe/Guernsey' || tz === 'Europe/Isle_of_Man' || tz === 'Europe/Jersey') _cachedRegion = 'UK'
    else if (tz.startsWith('America/') || tz.startsWith('US/') || tz === 'Pacific/Honolulu') _cachedRegion = 'US'
    else _cachedRegion = 'AU'
  } catch {
    _cachedRegion = 'AU'
  }
  return _cachedRegion
}

/** Persist the user's region selection and update the cache */
export function setRegion(region: Region): void {
  _cachedRegion = region
  try {
    localStorage.setItem(REGION_STORAGE_KEY, region)
  } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent('ssdl:region-change', { detail: region }))
}

// ── Unit conversion helpers (US imperial vs metric) ──

const KG_TO_LBS = 2.20462
const ML_TO_FL_OZ = 0.033814

/** Returns true if the current region uses imperial units */
export function isImperial(): boolean {
  return detectRegion() === 'US'
}

/** Weight unit label for current region */
export function weightUnit(): string {
  return isImperial() ? 'lbs' : 'kg'
}

/** Volume unit label for current region */
export function volumeUnit(): string {
  return isImperial() ? 'fl oz' : 'ml'
}

/** Convert kg to display weight (lbs for US, kg for others) */
export function kgToDisplay(kg: number): number {
  return isImperial() ? Math.round(kg * KG_TO_LBS * 10) / 10 : kg
}

/** Convert display weight back to kg */
export function displayToKg(value: number): number {
  return isImperial() ? Math.round(value / KG_TO_LBS * 10) / 10 : value
}

/** Convert ml to display volume (fl oz for US, ml for others) */
export function mlToDisplay(ml: number): number {
  return isImperial() ? Math.round(ml * ML_TO_FL_OZ * 10) / 10 : ml
}

/** Convert display volume back to ml */
export function displayToMl(value: number): number {
  return isImperial() ? Math.round(value / ML_TO_FL_OZ * 10) / 10 : value
}

/** Format date as dd/mm/yyyy (AU/UK/NZ) or mm/dd/yyyy (US) */
export function formatCompactDate(dateStr: string): string {
  const d = new Date(dateStr)
  const day = d.getDate().toString().padStart(2, '0')
  const month = (d.getMonth() + 1).toString().padStart(2, '0')
  const year = d.getFullYear()
  if (detectRegion() === 'US') return `${month}/${day}/${year}`
  return `${day}/${month}/${year}`
}

/**
 * Format date for history table: "Wed 18-Feb-26 11:45" (desktop) or "dd/mm/yyyy" (mobile)
 */
export function formatHistoryDate(dateStr: string): string {
  if (isMobile()) return formatCompactDate(dateStr)
  const d = new Date(dateStr)
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  const dayName = days[d.getDay()]
  const date = d.getDate().toString().padStart(2, '0')
  const month = months[d.getMonth()]
  const year = d.getFullYear().toString().slice(-2)
  const hours = d.getHours().toString().padStart(2, '0')
  const minutes = d.getMinutes().toString().padStart(2, '0')

  return `${dayName} ${date}-${month}-${year} ${hours}:${minutes}`
}

/**
 * Format date with ordinal suffix: "Fri 20th Feb '26" (desktop) or "dd/mm/yyyy" (mobile)
 */
export function formatOrdinalDate(dateStr: string): string {
  if (isMobile()) return formatCompactDate(dateStr)
  const d = new Date(dateStr)
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  const dayName = days[d.getDay()]
  const date = d.getDate()
  const month = months[d.getMonth()]
  const year = d.getFullYear().toString().slice(-2)

  const suffix = (date >= 11 && date <= 13) ? 'th'
    : date % 10 === 1 ? 'st'
    : date % 10 === 2 ? 'nd'
    : date % 10 === 3 ? 'rd'
    : 'th'

  return `${dayName} ${date}${suffix} ${month} '${year}`
}

/**
 * Parse a time duration string (e.g., "1h 30m") to seconds.
 */
export function parseDuration(durationStr: string): number {
  let seconds = 0

  const hoursMatch = durationStr.match(/(\d+)\s*h/i)
  if (hoursMatch) seconds += parseInt(hoursMatch[1]) * 3600

  const minutesMatch = durationStr.match(/(\d+)\s*m/i)
  if (minutesMatch) seconds += parseInt(minutesMatch[1]) * 60

  const secsMatch = durationStr.match(/(\d+)\s*s/i)
  if (secsMatch) seconds += parseInt(secsMatch[1])

  return seconds
}
